/**
 * Surface Poly — Object Mode tool for placing an N-gon prism by drawing
 * a closed polyline on a face, then extruding height.
 *
 * Three-stage modal interaction, mirrors `SurfaceBoxHandler` conventions:
 *
 *   1. Stage `select`: click a face to lock an oriented surface grid. Shift
 *      click up to two joining faces on the same object to average the grid
 *      normal across an edge or corner.
 *
 *   2. Stage `drawing`: the next drag places the first polyline segment on
 *      the selected plane. Each subsequent click+drag must START at one of
 *      the polyline's two free endpoints (head or tail) and ends at a new
 *      vertex. Both endpoints are live — the polyline grows from either end.
 *      Snap (vertex / edge / face / surface / grid) projects onto the locked
 *      plane. Shift constrains the new edge angle to the nearest 45°
 *      increment, anchored at the drag-start endpoint.
 *
 *      Camera orbit is suppressed during this stage. As soon as the loop
 *      closes the active flag is cleared and the camera unlocks so the
 *      user can orbit before extruding.
 *
 *   3. Stage `extrude`: pointer ownership transfers to `SurfacePolyGizmo`.
 *      Pulling the silhouette into a prism requires grabbing the gizmo
 *      arrow — same pattern as Edit Mode's extrude. This handler does not
 *      touch pointer events in the extrude stage; it only owns the cancel
 *      paths (Escape / blur / pointercancel) and the segment-level
 *      undo / redo bridge.
 *
 * Self-intersection refused: a candidate segment that would cross any
 * non-adjacent existing segment renders red and is rejected on release.
 *
 * Per-modal undo / redo: handler-local segment stack. Backspace, Cmd-Z,
 * Cmd-Y all route through the same pop / replay path. Below-zero pop
 * cancels the modal. None of this touches the global undo stack —
 * `pushUndo()` is called only inside `confirmSurfacePoly`.
 */

import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import type {
  SurfacePolyDraftState,
  SurfacePolyPreview,
} from "../app/storeTypes.ts";
import { selectSurfaceToolOfKind } from "../app/surfaceToolSelectors.ts";
import { intersectRayWithBasisPlane } from "../modeling/basisPlaneMath.ts";
import type { FaceBasis } from "../modeling/faceBasis.ts";
import {
  projectPointOntoPlane,
  querySnap,
  setLastSnapResult,
} from "../modeling/snap.ts";
import { basisUV, uvToWorld } from "../modeling/surfaceBoxMath.ts";
import {
  DRAW_DRAG_THRESHOLD_PX,
  SURFACE_CONFIRM_EPSILON,
} from "../modeling/surfaceConstants.ts";
import {
  clamp45InBasis,
  segmentSelfIntersects,
  type UV,
} from "../modeling/surfacePolyMath.ts";
import type { ViewportSurfaceHit } from "../modeling/viewportSurfaceRaycast.ts";
import { createRafCoalescer } from "../rafCoalescer.ts";
import {
  dynamicSurfaceGridSpacing,
  isCoarsePointer,
  ndcFromClient,
  pointerShiftActive,
  snapPointToSurfaceGrid,
  worldToScreenPx as worldToScreenPxShared,
} from "./surfaceModalUtils.ts";
import {
  registerSurfacePolyUndoRedo,
  setSurfacePolyHoveredEndpoint,
} from "./surfacePolyUndoBridge.ts";
import {
  clearSurfaceSelectionHighlight,
  setSurfaceSelectionHighlightHover,
  setSurfaceSelectionHighlightSelection,
} from "./surfaceSelectionHighlight.ts";
import {
  resolveJoinedSurfaceSelectionAtPointer,
  resolveSurfaceSelectionAtPointer,
} from "./surfaceSelectionInput.ts";

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const EMPTY_EXCLUDE = new Set<string>();
const DEFAULT_CLOSURE_PX = 12;
const COARSE_CLOSURE_PX = 18;
const DEFAULT_ENDPOINT_HIT_PX = 14;
const COARSE_ENDPOINT_HIT_PX = 22;

type EndpointId = "head" | "tail";

interface SegmentOp {
  /** "addVertex" appends/prepends a vertex; "close" marks the loop closed. */
  kind: "addVertex" | "close";
  /** Vertex placed (for "close" this equals the opposite endpoint). */
  vertex: [number, number, number];
  /** Which end the vertex was added at (irrelevant for "close"). */
  end: EndpointId;
  /** Snapshot of `height` at the moment this op fired (for "close" only). */
  heightAtClose?: number;
}

// Shared module-level scratch for world→screen projection. Two surface
// handlers used to keep their own; the worldToScreenPxShared helper takes
// it as an explicit argument to remain allocation-free.
const _scratchProj = new THREE.Vector3();

function worldToScreenPx(
  world: readonly [number, number, number],
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number } {
  return worldToScreenPxShared(_scratchProj, world, camera, rect);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SurfacePolyHandler() {
  const { camera, gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;

    // -------------------------------------------------------------------
    // Local state — pointer / draft mirror lives outside the store to
    // avoid React re-renders during a 60Hz drag. The store gets a
    // snapshot on every state-changing event (vertex placed, preview
    // updated, etc.).
    // -------------------------------------------------------------------
    let pointerId = -1;
    let stage: "select" | "drawing" | "extrude" | null = null;
    let basis: FaceBasis | null = null;
    let hostObjectId: string | null = null;
    let primarySurfaceHit: ViewportSurfaceHit | null = null;
    let selectedSurfaceHits: ViewportSurfaceHit[] = [];
    let vertices: [number, number, number][] = [];
    let closed = false;
    let height = 0;
    let dragFromEnd: EndpointId | null = null;
    let lastClientX = 0;
    let lastClientY = 0;
    let shiftHeld = false;
    let pendingFirstVertex: {
      point: [number, number, number] | null;
      clientX: number;
      clientY: number;
    } | null = null;
    let prevCursor = "";

    // Per-modal undo / redo (segment level).
    const undoStack: SegmentOp[] = [];
    const redoStack: SegmentOp[] = [];

    // Reusable scratch — avoids per-frame Vector3 allocation in the rAF
    // loop. Mirrors `SurfaceBoxHandler`'s scratch pool.
    const _scratchVecA = new THREE.Vector3();
    const _scratchVecB = new THREE.Vector3();
    const _scratchVecC = new THREE.Vector3();
    const _scratchVecOrigin = new THREE.Vector3();
    const _scratchVecDirection = new THREE.Vector3();

    // -------------------------------------------------------------------
    // Helpers — declared up-front so the event handlers below can
    // reference them in any order. Bodies filled in by subsequent edits.
    // -------------------------------------------------------------------
    function isArmed(): boolean {
      const s = useStore.getState();
      return (
        s.activeOperator === "surface_poly" && s.workspaceMode === "object"
      );
    }

    function publishDraft(preview: SurfacePolyPreview | null) {
      if (!basis) return;
      const cur = selectSurfaceToolOfKind(useStore.getState(), "poly");
      const draft: SurfacePolyDraftState = {
        basis,
        hostObjectId,
        vertices: vertices.map((v) => [v[0], v[1], v[2]]),
        closed,
        preview,
        height,
        profileTarget: cur?.draft?.profileTarget ?? { kind: "face" },
      };
      // First write of the modal initialises the slot (kind/stage/active);
      // subsequent writes patch the draft in place.
      if (cur) {
        useStore.getState().setSurfaceTool({ ...cur, draft });
      } else {
        useStore.getState().setSurfaceTool({
          kind: "poly",
          stage: "select",
          draft,
          active: true,
        });
      }
    }

    function publishSelectedSurfaceDraft() {
      publishDraft(null);
    }

    function selectSurfaceAtPointer(e: PointerEvent): boolean {
      const result = resolveSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
      );
      if (!result) return false;
      basis = result.basis;
      hostObjectId = result.hostObjectId;
      primarySurfaceHit = result.hit;
      selectedSurfaceHits = [result.hit];
      setSurfaceSelectionHighlightSelection(selectedSurfaceHits);
      setSurfaceSelectionHighlightHover(null);
      vertices = [];
      closed = false;
      height = 0;
      publishSelectedSurfaceDraft();
      return true;
    }

    function joinSurfaceAtPointer(e: PointerEvent): boolean {
      if (!primarySurfaceHit || selectedSurfaceHits.length >= 3) return false;
      const result = resolveJoinedSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
        selectedSurfaceHits,
      );
      if (!result) return false;
      basis = result.basis;
      hostObjectId = result.hostObjectId;
      selectedSurfaceHits = [...selectedSurfaceHits, result.hit];
      setSurfaceSelectionHighlightSelection(selectedSurfaceHits);
      setSurfaceSelectionHighlightHover(null);
      publishSelectedSurfaceDraft();
      return true;
    }

    function updateSurfaceHoverAtPointer(e: PointerEvent) {
      if (!isArmed()) return;
      if (stage !== null && stage !== "select") {
        setSurfaceSelectionHighlightHover(null);
        return;
      }
      const result = resolveSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
      );
      setSurfaceSelectionHighlightHover(result?.hit ?? null);
    }

    function pointOnSelectedSurface(
      e: PointerEvent,
    ): [number, number, number] | null {
      if (!basis) return null;
      const rect = canvas.getBoundingClientRect();
      const { ndcX, ndcY } = ndcFromClient(e.clientX, e.clientY, rect);
      const state = useStore.getState();

      if (state.snapEnabled) {
        const snap = querySnap(ndcX, ndcY, state.snapTarget, EMPTY_EXCLUDE, 0);
        setLastSnapResult(snap);
        if (snap) {
          const projected = projectPointOntoPlane(
            snap.worldPosition,
            basis.origin,
            basis.normal,
            _scratchVecA,
          );
          return [projected.x, projected.y, projected.z];
        }
        if (state.snapTarget === "grid") {
          const hit = intersectRayWithBasisPlane(
            ndcX,
            ndcY,
            camera,
            basis,
            _scratchVecB,
            _scratchVecOrigin,
            _scratchVecDirection,
          );
          if (hit) {
            return snapPointToSurfaceGrid(
              basis,
              [hit.x, hit.y, hit.z],
              dynamicSurfaceGridSpacing(state.gridLogDist),
            );
          }
        }
      }

      const hit = intersectRayWithBasisPlane(
        ndcX,
        ndcY,
        camera,
        basis,
        _scratchVecC,
        _scratchVecOrigin,
        _scratchVecDirection,
      );
      return hit ? [hit.x, hit.y, hit.z] : null;
    }

    function reset(restoreCursor = true) {
      coalescer.cancel();
      if (pointerId !== -1) {
        try {
          canvas.releasePointerCapture(pointerId);
        } catch {
          // Capture may already be lost (pointercancel, blur).
        }
        pointerId = -1;
      }
      if (restoreCursor && stage !== null) {
        canvas.style.cursor = prevCursor;
      }
      stage = null;
      basis = null;
      hostObjectId = null;
      primarySurfaceHit = null;
      selectedSurfaceHits = [];
      vertices = [];
      pendingFirstVertex = null;
      closed = false;
      height = 0;
      dragFromEnd = null;
      shiftHeld = false;
      undoStack.length = 0;
      redoStack.length = 0;
      setLastSnapResult(null);
      setSurfacePolyHoveredEndpoint(null);
      clearSurfaceSelectionHighlight();
      // Clear the slot entirely rather than zeroing fields in place —
      // see SurfaceBoxHandler's `reset` for the same rationale (publish-
      // Draft only re-initialises stage="select" when the slot is null).
      const st = useStore.getState();
      if (selectSurfaceToolOfKind(st, "poly")) {
        st.setSurfaceTool(null);
      }
    }

    function endpointAtPointer(
      clientX: number,
      clientY: number,
    ): EndpointId | null {
      if (vertices.length === 0) return null;
      const rect = canvas.getBoundingClientRect();
      const threshold = isCoarsePointer()
        ? COARSE_ENDPOINT_HIT_PX
        : DEFAULT_ENDPOINT_HIT_PX;
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const head = worldToScreenPx(vertices[0], camera, rect);
      const headDist = Math.hypot(head.x - px, head.y - py);
      // For a single-vertex polyline both ends collapse — treat as "head".
      if (vertices.length === 1) {
        return headDist < threshold ? "head" : null;
      }
      const tail = worldToScreenPx(vertices[vertices.length - 1], camera, rect);
      const tailDist = Math.hypot(tail.x - px, tail.y - py);
      const headIn = headDist < threshold;
      const tailIn = tailDist < threshold;
      if (!headIn && !tailIn) return null;
      if (headIn && (!tailIn || headDist <= tailDist)) return "head";
      return "tail";
    }

    function recomputeHover(clientX: number, clientY: number) {
      // Hover is meaningful only during the drawing stage and only when
      // no drag is in progress. The gizmo polls the bridge slot; setting
      // it to null when out of range keeps the dot at default brightness.
      if (stage !== "drawing" || pointerId !== -1) {
        setSurfacePolyHoveredEndpoint(null);
        return;
      }
      setSurfacePolyHoveredEndpoint(endpointAtPointer(clientX, clientY));
    }

    function computeCandidate(
      clientX: number,
      clientY: number,
    ): {
      world: [number, number, number];
      uv: UV;
      willClose: boolean;
      selfIntersects: boolean;
    } | null {
      if (!basis || dragFromEnd === null) return null;
      const rect = canvas.getBoundingClientRect();
      const { ndcX, ndcY } = ndcFromClient(clientX, clientY, rect);
      const state = useStore.getState();

      // 1. Snap pipeline — vertex/edge/face/surface results are projected
      //    back onto the locked plane; grid snap rounds (u, v) to the
      //    dynamic spacing on the plane. Same shape as Surface Box.
      let candidateWorld: [number, number, number] | null = null;
      if (state.snapEnabled) {
        const snap = querySnap(ndcX, ndcY, state.snapTarget, EMPTY_EXCLUDE, 0);
        setLastSnapResult(snap);
        if (snap) {
          const projected = projectPointOntoPlane(
            snap.worldPosition,
            basis.origin,
            basis.normal,
            _scratchVecA,
          );
          candidateWorld = [projected.x, projected.y, projected.z];
        } else if (state.snapTarget === "grid") {
          const hit = intersectRayWithBasisPlane(
            ndcX,
            ndcY,
            camera,
            basis,
            _scratchVecB,
            _scratchVecOrigin,
            _scratchVecDirection,
          );
          if (hit) {
            candidateWorld = snapPointToSurfaceGrid(
              basis,
              [hit.x, hit.y, hit.z],
              dynamicSurfaceGridSpacing(state.gridLogDist),
            );
          }
        }
      }
      if (!candidateWorld) {
        const hit = intersectRayWithBasisPlane(
          ndcX,
          ndcY,
          camera,
          basis,
          _scratchVecC,
          _scratchVecOrigin,
          _scratchVecDirection,
        );
        if (!hit) return null;
        candidateWorld = [hit.x, hit.y, hit.z];
      }

      // 2. 45° clamp (Shift) — done in basis (u, v) so a snapped corner
      //    keeps its snap target whenever Shift releases. Anchored at the
      //    drag-start endpoint.
      const startWorld =
        dragFromEnd === "tail" ? vertices[vertices.length - 1] : vertices[0];
      const startUV = basisUV(basis, startWorld);
      let candidateUV = basisUV(basis, candidateWorld);
      if (shiftHeld) {
        candidateUV = clamp45InBasis(startUV, candidateUV);
        candidateWorld = uvToWorld(basis, candidateUV.u, candidateUV.v);
      }

      // 3. Closure detection — only when ≥3 vertices already placed and
      //    the candidate lands within the screen-space threshold of the
      //    OPPOSITE endpoint. Snap to the exact opposite endpoint so the
      //    closing seam aligns to a single point.
      let willClose = false;
      if (vertices.length >= 3) {
        const oppositeWorld =
          dragFromEnd === "tail" ? vertices[0] : vertices[vertices.length - 1];
        const oppScreen = worldToScreenPx(oppositeWorld, camera, rect);
        const candScreen = worldToScreenPx(candidateWorld, camera, rect);
        const dPx = Math.hypot(
          oppScreen.x - candScreen.x,
          oppScreen.y - candScreen.y,
        );
        const closurePx = isCoarsePointer()
          ? COARSE_CLOSURE_PX
          : DEFAULT_CLOSURE_PX;
        if (dPx < closurePx) {
          willClose = true;
          candidateWorld = [
            oppositeWorld[0],
            oppositeWorld[1],
            oppositeWorld[2],
          ];
          candidateUV = basisUV(basis, candidateWorld);
        }
      }

      // 4. Self-intersection — every existing edge except those adjacent
      //    to the candidate's start (and, when closing, the candidate's
      //    end). Worth doing even on closure: a closing segment that
      //    crosses prior edges would build a figure-8 / non-simple
      //    polygon that earcut can't triangulate cleanly.
      const lockedBasis = basis;
      const polylineUV = vertices.map((v) => basisUV(lockedBasis, v));
      const mode: "appendTail" | "appendHead" | "close" = willClose
        ? "close"
        : dragFromEnd === "tail"
          ? "appendTail"
          : "appendHead";
      const selfIntersects = segmentSelfIntersects(polylineUV, {
        from: startUV,
        to: candidateUV,
        mode,
      });

      return {
        world: candidateWorld,
        uv: candidateUV,
        willClose,
        selfIntersects,
      };
    }

    function applyOpForward(op: SegmentOp) {
      if (op.kind === "addVertex") {
        if (op.end === "head") {
          vertices.unshift([...op.vertex]);
        } else {
          vertices.push([...op.vertex]);
        }
      } else {
        // close: just flip the flag — vertices already contain the loop.
        closed = true;
        if (typeof op.heightAtClose === "number") {
          height = op.heightAtClose;
        }
      }
    }

    function applyOpInverse(op: SegmentOp) {
      if (op.kind === "addVertex") {
        if (op.end === "head") {
          vertices.shift();
        } else {
          vertices.pop();
        }
      } else {
        closed = false;
        height = 0;
      }
    }

    function pushSegment(op: SegmentOp) {
      applyOpForward(op);
      undoStack.push(op);
      redoStack.length = 0;
      // Stage transition on close hands pointer ownership to
      // `SurfacePolyGizmo` for the extrude phase — see `applyStageTransition`.
      const wantStage: "drawing" | "extrude" = closed ? "extrude" : "drawing";
      if (stage !== wantStage) {
        stage = wantStage;
        applyStageTransition(wantStage);
      }
      publishDraft(null);
    }

    function popSegment(): boolean {
      const op = undoStack.pop();
      if (!op) return false;
      applyOpInverse(op);
      redoStack.push(op);
      // A "close" pop drops us back from extrude -> drawing without
      // touching the polyline. Popping the first vertex returns to the
      // selected-surface stage and keeps the oriented grid available.
      const wantStage: "select" | "drawing" | "extrude" =
        vertices.length === 0 && !closed
          ? "select"
          : closed
            ? "extrude"
            : "drawing";
      if (stage !== wantStage) {
        stage = wantStage;
        applyStageTransition(wantStage);
      }
      // If the polyline emptied, we still hold the basis — but the
      // visual is empty; publish so the gizmo clears.
      publishDraft(null);
      return true;
    }

    function replaySegment(): boolean {
      const op = redoStack.pop();
      if (!op) return false;
      applyOpForward(op);
      undoStack.push(op);
      const wantStage: "drawing" | "extrude" = closed ? "extrude" : "drawing";
      if (stage !== wantStage) {
        stage = wantStage;
        applyStageTransition(wantStage);
      }
      publishDraft(null);
      return true;
    }

    // Sync store stage + active flag + cursor for a stage change. Drawing
    // re-engages canvas pointer capture (active=true, crosshair); extrude
    // hands ownership to the gizmo (active=false, default cursor) so the
    // camera can orbit freely.
    function applyStageTransition(next: "select" | "drawing" | "extrude") {
      const st = useStore.getState();
      const cur = selectSurfaceToolOfKind(st, "poly");
      if (!cur) return;
      const active = next !== "extrude";
      st.setSurfaceTool({ ...cur, stage: next, active });
      if (next === "extrude") {
        canvas.style.cursor = prevCursor;
      } else {
        canvas.style.cursor = "crosshair";
        setSurfacePolyHoveredEndpoint(null);
      }
    }

    // -------------------------------------------------------------------
    // rAF coalescer — pointermove targets at most once per frame to keep
    // the snap raycast + intersection pass off the per-event hot path.
    // -------------------------------------------------------------------
    const coalescer = createRafCoalescer<{ x: number; y: number }>(
      ({ x, y }) => {
        lastClientX = x;
        lastClientY = y;
        if (stage === "drawing" && pointerId !== -1 && dragFromEnd !== null) {
          const cand = computeCandidate(x, y);
          if (!cand) {
            publishDraft(null);
            return;
          }
          publishDraft({
            fromEnd: dragFromEnd,
            candidate: cand.world,
            selfIntersects: cand.selfIntersects,
            willClose: cand.willClose,
          });
        } else if (stage === "drawing") {
          recomputeHover(x, y);
        }
      },
    );

    // -------------------------------------------------------------------
    // Pointer event handlers
    // -------------------------------------------------------------------
    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      if (!isArmed()) return;
      if (pointerId !== -1) return;
      // The extrude stage is owned by the gizmo. The canvas stays free for
      // orbit so the user can reframe before grabbing the arrow.
      if (stage === "extrude") return;

      if (stage === null) {
        // selectSurfaceAtPointer initialises the surfaceTool slot through
        // publishSelectedSurfaceDraft (kind "poly", stage "select",
        // active true) — no follow-up patch is needed here.
        if (!selectSurfaceAtPointer(e)) return;
        e.preventDefault();
        e.stopPropagation();
        prevCursor = canvas.style.cursor;
        canvas.style.cursor = "crosshair";
        stage = "select";
        return;
      }

      if (stage === "select") {
        e.preventDefault();
        e.stopPropagation();
        const startPoint = pointOnSelectedSurface(e);

        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          // Capture may fail on some PointerEvents shims — proceed anyway.
        }
        pointerId = e.pointerId;
        pendingFirstVertex = {
          point: startPoint
            ? [startPoint[0], startPoint[1], startPoint[2]]
            : null,
          clientX: e.clientX,
          clientY: e.clientY,
        };
        shiftHeld = pointerShiftActive(e);
        lastClientX = e.clientX;
        lastClientY = e.clientY;
        canvas.style.cursor = "crosshair";
        return;
      }

      if (stage === "drawing") {
        // ----- Subsequent drag must start at an endpoint -------------
        const end = endpointAtPointer(e.clientX, e.clientY);
        if (end === null) return; // not on an endpoint → orbit / select claims the gesture
        e.preventDefault();
        e.stopPropagation();
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        pointerId = e.pointerId;
        dragFromEnd = end;
        shiftHeld = pointerShiftActive(e);
        lastClientX = e.clientX;
        lastClientY = e.clientY;
        // Drive an immediate preview so the user sees the rubber band
        // even before the first pointermove.
        coalescer.schedule({ x: e.clientX, y: e.clientY });
        return;
      }
    }

    function onPointerMove(e: PointerEvent) {
      // Drag in flight → coalesce. Otherwise, drive endpoint hover so the
      // gizmo can highlight the dot the cursor would grab.
      if (pointerId !== -1) {
        if (e.pointerId !== pointerId) return;
        shiftHeld = pointerShiftActive(e);
        if (stage === "select" && pendingFirstVertex) {
          const dx = e.clientX - pendingFirstVertex.clientX;
          const dy = e.clientY - pendingFirstVertex.clientY;
          if (Math.hypot(dx, dy) < DRAW_DRAG_THRESHOLD_PX) return;
          if (!pendingFirstVertex.point) return;
          vertices = [[...pendingFirstVertex.point]];
          closed = false;
          height = 0;
          dragFromEnd = "tail";
          stage = "drawing";
          undoStack.push({
            kind: "addVertex",
            vertex: [...pendingFirstVertex.point],
            end: "tail",
          });
          pendingFirstVertex = null;
          setSurfaceSelectionHighlightHover(null);
          // applyStageTransition handles stage + active + cursor in one
          // place; mirrors what pushSegment / popSegment use for the
          // drawing↔extrude flips.
          applyStageTransition("drawing");
          publishDraft(null);
        }
        coalescer.schedule({ x: e.clientX, y: e.clientY });
        return;
      }
      if (stage === null || stage === "select") {
        updateSurfaceHoverAtPointer(e);
        return;
      }
      if (stage === "drawing") {
        coalescer.schedule({ x: e.clientX, y: e.clientY });
      }
    }

    function onPointerUp(e: PointerEvent) {
      if (e.button !== 0) return;
      if (e.pointerId !== pointerId) return;
      if (stage === "select" && pendingFirstVertex) {
        const joinClick = pointerShiftActive(e);
        try {
          canvas.releasePointerCapture(pointerId);
        } catch {
          // ignore
        }
        pointerId = -1;
        pendingFirstVertex = null;
        setLastSnapResult(null);
        shiftHeld = false;
        if (joinClick) {
          joinSurfaceAtPointer(e);
        } else {
          selectSurfaceAtPointer(e);
        }
        return;
      }
      // The extrude stage's confirm path lives on the gizmo.
      if (stage !== "drawing") return;
      coalescer.flush();

      if (dragFromEnd !== null && basis !== null) {
        const cand = computeCandidate(e.clientX, e.clientY);
        // Always release pointer capture between drawing-stage drags.
        try {
          canvas.releasePointerCapture(pointerId);
        } catch {
          // ignore
        }
        pointerId = -1;
        const fromEnd = dragFromEnd;
        dragFromEnd = null;

        if (!cand) {
          publishDraft(null);
          return;
        }
        // Reject self-intersecting candidates outright — this includes
        // the closing variant. The polyline stays as-is; the user can
        // try a different release point.
        if (cand.selfIntersects) {
          publishDraft(null);
          return;
        }
        // Reject sub-epsilon segments (drag landed back on the start).
        const startWorld =
          fromEnd === "tail" ? vertices[vertices.length - 1] : vertices[0];
        const dx = cand.world[0] - startWorld[0];
        const dy = cand.world[1] - startWorld[1];
        const dz = cand.world[2] - startWorld[2];
        if (Math.hypot(dx, dy, dz) < SURFACE_CONFIRM_EPSILON) {
          publishDraft(null);
          return;
        }

        if (cand.willClose) {
          pushSegment({
            kind: "close",
            vertex: [cand.world[0], cand.world[1], cand.world[2]],
            end: fromEnd,
          });
        } else {
          pushSegment({
            kind: "addVertex",
            vertex: [cand.world[0], cand.world[1], cand.world[2]],
            end: fromEnd,
          });
        }
      }
    }

    function onPointerCancel(e: PointerEvent) {
      if (e.pointerId !== pointerId && pointerId !== -1) return;
      reset();
    }

    function onPointerLeave() {
      setSurfaceSelectionHighlightHover(null);
    }

    function onWindowBlur() {
      reset();
    }

    function onKeyDown(e: KeyboardEvent) {
      // Don't intercept keys typed into form fields (search palette, etc).
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "Escape") {
        if (stage === null && !isArmed()) return;
        e.preventDefault();
        if (stage === null) {
          useStore.getState().armToolOperator(null);
          return;
        }
        reset();
        return;
      }
      if (stage === null) return;
      if (e.key === "Backspace") {
        e.preventDefault();
        // Same policy as the global Cmd-Z route through the bridge: pop
        // a segment, or cancel the modal when the stack is empty.
        if (!popSegment()) reset();
        return;
      }
      if (e.key === "Shift" && !shiftHeld) {
        shiftHeld = true;
        // Re-coalesce with the cached pointer so the 45° clamp updates
        // even if the cursor isn't moving.
        if (stage === "drawing" && pointerId !== -1) {
          coalescer.schedule({ x: lastClientX, y: lastClientY });
        }
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      if (e.key === "Shift" && shiftHeld) {
        shiftHeld = false;
        if (stage === "drawing" && pointerId !== -1) {
          coalescer.schedule({ x: lastClientX, y: lastClientY });
        }
      }
    }

    // -------------------------------------------------------------------
    // Bridge registration (Cmd-Z / Cmd-Shift-Z route through the store
    // → surfacePolyUndoBridge → these closures).
    // -------------------------------------------------------------------
    registerSurfacePolyUndoRedo(
      () => popSegment(),
      () => replaySegment(),
    );

    // -------------------------------------------------------------------
    // External disarm subscription — palette toggle / undo-past-zero /
    // any other path that clears `activeOperator` while the modal is
    // mid-flight needs to restore the cursor and release pointer capture.
    // -------------------------------------------------------------------
    const unsubscribe = useStore.subscribe((s, prev) => {
      if (
        prev.activeOperator === "surface_poly" &&
        s.activeOperator !== "surface_poly"
      ) {
        reset();
      }
    });

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);
    canvas.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);

    return () => {
      reset();
      registerSurfacePolyUndoRedo(null, null);
      setSurfacePolyHoveredEndpoint(null);
      unsubscribe();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [camera, gl]);

  return null;
}
